import Frame from './Frame'

export default class Layer {
  constructor(data, loadCurrentFrame = true) {
    const defaults = {
      frames: {},
      selectedOption: null,
      options: null,
      hasOptions: true,
      pathTemplate: null,
      frameCount: 72,
      imgproxySettings: [],
      useMobileImages: true,
      staticAngle: false,
      mode: 'on',
      keyAngles: [],
      useStaticPathTemplate: false,
      useTestPathTemplate: false,
    }

    Object.assign(this, defaults, data)

    this.setSelectedOption(data.selectedOption)

    if (!loadCurrentFrame) return this

    if (this.eventBus)
      this.eventBus.on('load', this.handleFrameLoad.bind(this))

    const frame = this.loadCurrentFrame()
    return frame
  }

  setSelectedOption(option) {
    this.selectedOption = option
    this.updateFrameCounts()
  }

  updateFrameCounts() {
    this.currentDesignFrameCount = this._currentDesignFrameCount
    this.currentDesignFramesLoaded = this._currentDesignFramesLoaded
    this.eventBus?.emit('update-load-state')
  }

  loadCurrentFrame() {
    if (this.options === null) return this
    const hasSelectedOption =
      this.selectedOption && this.selectedOption in this.options
    if (!hasSelectedOption && this.hasOptions !== false) return this

    this.frames[this.frameIdx] = this._preload(
      this.frameNumber,
      this.selectedOption
    )

    return new Promise((resolve) => {
      const wait = () => {
        if (this.currentFrameLoaded) {
          return resolve(this)
        }
        setTimeout(wait, 50)
      }
      wait()
    })
  }

  handleFrameLoad(frameIdx) {
    if (!frameIdx.startsWith(`${this.id}:${this.selectedOption}:`)) return
    this.currentDesignFramesLoaded += 1
    this.eventBus?.emit('update-load-state')
  }

  get allFrameNumbers() {
    if (this.staticAngle) return [this.frameNumber]
    if (this.mode === 'off') return this.keyAngles

    const allFrameNumbers = [...Array(this.frameCount)].map((_, idx) => idx + 1)

    // In limited mode then select ever other frame plus key angles
    if (this.mode === 'limited') {
      return [
        ...new Set([
          ...allFrameNumbers.filter((_, idx) => idx % 2 === 0),
          ...this.keyAngles,
        ]),
      ].sort((a, b) => a - b)
    }

    return allFrameNumbers
  }

  preload() {
    // Preload all frames for the current selection
    let currentDesign = []
    if (!this.staticAngle) {
      currentDesign = this.allFrameNumbers
        .map((frameNumber) => {
          if (!this.selectedOption && this.hasOptions !== false) return null

          const frameIndex = this._getFrameIdx(frameNumber, this.selectedOption)
          if (frameIndex in this.frames) return null

          return [frameIndex, this._preload(frameNumber, this.selectedOption)]
        })
        .filter((entry) => entry)
    }

    // Preload all selections for the current frame
    const otherDesigns = Object.keys(this.resolvedOptions)
      .map((optionKey) => {
        const frameIndex = this._getFrameIdx(this.frameNumber, optionKey)
        if (frameIndex in this.frames) return null

        return [frameIndex, this._preload(this.frameNumber, optionKey)]
      })
      .filter((entry) => entry)

    this.frames = {
      ...this.frames,
      ...Object.fromEntries(currentDesign),
      ...Object.fromEntries(otherDesigns),
    }

    this.updateFrameCounts()

    return new Promise((resolve) => {
      const wait = () => {
        if (this.allFramesLoaded) {
          return resolve(this)
        }
        setTimeout(wait, 50)
      }
      wait()
    })
  }

  preloadAll() {
    this.frames = {
      ...this.frames,
      ...Object.keys(this.resolvedOptions)
        .map((optionKey) => {
          return [...Array(this.frameCount)].map((_, idx) => {
            const frameIndex = this._getFrameIdx(idx + 1, optionKey)
            if (frameIndex in this.frames) return null

            return [frameIndex, this._preload(idx + 1, optionKey)]
          })
        })
        .flat()
        .filter((entry) => entry),
    }

    this.updateFrameCounts()

    return new Promise((resolve) => {
      const wait = () => {
        if (this.allFramesLoaded) {
          return resolve(this)
        }
        setTimeout(wait, 50)
      }
      wait()
    })
  }

  _preload(frameNumber, optionKey, loadOnConstruct = true) {
    if (this.hasOptions && optionKey === null) return { disabled: true }

    const option = this.resolvedOptions[optionKey || 'default']
    const pathTemplate = option?.pathTemplate
    const staticPathTemplate = option?.staticPathTemplate
    const testPathTemplate = option?.testPathTemplate

    if (!option)
      console.log(`Unknown option '${optionKey}' in layer '${this.name}'`)

    return new Frame({
      frameNumber,
      pathTemplate,
      staticPathTemplate,
      testPathTemplate,
      disabled: option?.disabled,
      desktopImgproxySettings: this.imgproxySettings,
      mobileImgproxySettings: this.mobileImgproxySettings,
      useMobileImages: this.useMobileImages,
      imageSizeOverride: this.imageSizeOverride,
      id: `${this.id}:${this._getFrameIdx(frameNumber, optionKey)}`,
      eventBus: this.eventBus,
      loadOnConstruct,
      useStaticPathTemplate: this.useStaticPathTemplate,
      useTestPathTemplate: this.useTestPathTemplate
    })
  }

  get resolvedOptions() {
    if (this.options) {
      return this.options
    }
    return {
      default: {
        pathTemplate: this.pathTemplate,
      },
    }
  }

  get frameIdx() {
    return this._getFrameIdx(this.frameNumber, this.selectedOption)
  }

  _getFrameIdx(frameNumber, optionKey) {
    if (!this.allFrameNumbers.includes(frameNumber)) {
      // If this frameNumber isn't valid then use the closest valid one
      frameNumber = this.allFrameNumbers.reduce((prev, curr) => {
        return Math.abs(curr - frameNumber) < Math.abs(prev - frameNumber)
          ? curr
          : prev
      })
    }

    return `${optionKey || 'default'}:${frameNumber}`
  }

  get totalFrames() {
    return Object.values(this.frames).filter((frame) => !frame.disabled).length
  }

  get numberFramesLeftToLoad() {
    return Object.values(this.frames).filter(
      (frame) => !frame.loaded && !frame.disabled
    ).length
  }

  get _currentDesignFrames() {
    return Object.entries(this.frames).filter(([frameIdx, frame]) => {
      if (frame.disabled) return false
      if (!frameIdx.startsWith(`${this.selectedOption || 'default'}:`))
        return false
      return true
    })
  }

  get _currentDesignFrameCount() {
    return this._currentDesignFrames.length
  }

  get _currentDesignFramesLoaded() {
    return this._currentDesignFrames.filter(
      ([_frameIdx, frame]) => frame.loaded || frame.disabled
    ).length
  }

  get allFramesLoaded() {
    return Object.values(this.frames).every(
      (frame) => frame.loaded || frame.disabled
    )
  }

  get currentFrameLoaded() {
    return (
      this.frames[this.frameIdx]?.disabled || this.frames[this.frameIdx]?.loaded
    )
  }

  get currentFrameDisabled() {
    return this.frames[this.frameIdx]?.disabled
  }

  get img() {
    return this.frames[this.frameIdx]?.img
  }

  get currentFramePath() {
    if (this.frameIdx in this.frames) {
      return this.frames[this.frameIdx]?.path
    }

    return this._preload(this.frameNumber, this.selectedOption, false)?.path
  }
}
